/**
 * planet-structure-sprites.ts — v4M4.b — per-StructureKind commissioned
 * bitmap sprite cache + loader for on-planet structures.
 *
 * Architectural twin of `space-structure-sprites.ts` (same milestone)
 * and `ship-sprites.ts` (v4M4.a). Differs only in the cache key type
 * (`StructureKind` instead of free-form chassis string) and the URL
 * namespace (`/assets/structures/planet-*` vs `/assets/structures/space-*`).
 *
 * Consumer: `system-view.ts → drawStructureGlyph`. The procedural
 * 15-case switch keeps the procedural fallback intact when no sprite
 * is loaded for the kind. The icon-ring caller in
 * `drawStructureIconRing` sets `globalAlpha` (40% during construction)
 * + stroke / fill state before invoking the glyph, so the sprite
 * blit inherits those values transparently — under-construction
 * fade-out still works for bitmap structures, no caller change
 * needed.
 *
 * Sprite authoring contract (per `GRAPHICS.md §4.2`):
 *   - 64×64 PNG, RGBA, transparent background.
 *   - Centred at (32, 32).
 *   - Designed to read at ~14 px screen size (the icon-ring footprint
 *     per `GRAPHICS §6.1`); high-detail commissioned art will mostly
 *     read as silhouette at the target scale, so favour strong
 *     silhouette + bold internal contrast over fine line work.
 *   - Race-neutral colouring; the icon-ring stroke colour
 *     (`#9FB3D9` HUD-text-dim) is set by the procedural glyph path
 *     and does NOT tint the bitmap output. Bitmap structures pick
 *     their own internal palette.
 *
 * URL convention: `/assets/structures/planet-<kind>.png` where `kind`
 * is the `StructureKind` enum value verbatim (already kebab-case
 * in the type union; no transformation needed). Examples:
 * `planet-shipyard.png`, `planet-research-lab.png`,
 * `planet-planetary-shields.png`, `planet-energy-surface-battery.png`.
 */

import type { StructureKind } from '../state/types/index';

/**
 * Canonical sprite dimensions. Matches `space-structure-sprites.ts`
 * for consistency — both render at the same on-canvas scale.
 */
const STRUCTURE_SPRITE_SIZE = 64;

/** Cache key StructureKind → painted offscreen canvas. */
const planetStructureSpriteCache: Map<StructureKind, OffscreenCanvas> =
  new Map();

/**
 * Return the cached sprite for the given structure kind, or null when
 * no sprite has been loaded for it. Consumers (`system-view.ts`)
 * branch on this — null falls through to the procedural glyph.
 */
export function getPlanetStructureSprite(
  kind: StructureKind,
): OffscreenCanvas | null {
  return planetStructureSpriteCache.get(kind) ?? null;
}

/**
 * v4M4.b — load a commissioned planet-structure sprite for the given
 * kind. Painted into a 64×64 OffscreenCanvas regardless of source
 * dimensions; commissioned sprites should be authored at exactly
 * 64×64.
 *
 * Promise always resolves (no rejection): a failed load leaves the
 * existing cache entry untouched, the procedural fallback stays
 * active, and other in-flight loads in the batch continue.
 * Headless-safe: when `OffscreenCanvas` or `Image` is undefined the
 * load is a no-op.
 */
export function loadPlanetStructureSpriteImage(
  kind: StructureKind,
  url: string,
): Promise<void> {
  return new Promise((resolve) => {
    if (
      typeof OffscreenCanvas === 'undefined' ||
      typeof Image === 'undefined'
    ) {
      resolve();
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = new OffscreenCanvas(
          STRUCTURE_SPRITE_SIZE,
          STRUCTURE_SPRITE_SIZE,
        );
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve();
          return;
        }
        ctx.clearRect(0, 0, STRUCTURE_SPRITE_SIZE, STRUCTURE_SPRITE_SIZE);
        ctx.drawImage(img, 0, 0, STRUCTURE_SPRITE_SIZE, STRUCTURE_SPRITE_SIZE);
        planetStructureSpriteCache.set(kind, canvas);
      } catch {
        /* leave cache untouched — procedural fallback stays active */
      }
      resolve();
    };
    img.onerror = () => resolve();
    img.src = url;
  });
}

/**
 * Test-only — clear the sprite cache. Called from unit tests so that a
 * load in one test doesn't leak into the next.
 */
export function _resetPlanetStructureSpriteCacheForTests(): void {
  planetStructureSpriteCache.clear();
}
